import type { Time } from "@internationalized/date";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Heading } from "react-aria-components";
import z from "zod";

import { Button } from "@/components/base/button";
import {
  Disclosure,
  DisclosureHeader,
  DisclosurePanel,
} from "@/components/base/disclosure";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { Radio } from "@/components/base/radio-group";
import type { Division, TournamentDivision } from "@/db/schema";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";

import { useActiveTeams } from "../context";

const createPlayoffsSchema = z.object({
  id: z.number(),
  teamsPerPool: z.number(),
  wildcardCount: z.number(),
  assignCourts: z.boolean(),
  autoAssignWildcards: z.boolean(),
  overwrite: z.boolean(),
  sets: z.array(
    z.object({
      winScore: z.number(),
      switchScore: z.number(),
    }),
  ),
});

type MatchKind = "set" | "match";

export type CreatePlayoffsFormProps = {
  tournamentId: number;
  division: TournamentDivision & { division: Division };
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CreatePlayoffsForm({
  tournamentId,
  division,
  onOpenChange,
  ...props
}: CreatePlayoffsFormProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending, failureReason } = useMutation({
    ...trpc.tournaments.createPlayoffs.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());

      onOpenChange(false);
    },
  });

  const schema = createPlayoffsSchema
    .pick({
      teamsPerPool: true,
      wildcardCount: true,
      sets: true,
      assignCourts: true,
      autoAssignWildcards: true,
      overwrite: true,
    })
    .extend({
      matchKind: z.enum<MatchKind[]>(["set", "match"]),
      scheduledTime: z.any().nullish(),
    });

  const form = useAppForm({
    defaultValues: {
      teamsPerPool: 2,
      wildcardCount: 0,
      matchKind: "set" as MatchKind,
      sets: [
        {
          winScore: 28,
          switchScore: 7,
        },
      ],
      scheduledTime: null as Time | null,
      assignCourts: true,
      autoAssignWildcards: false,
      overwrite: false,
    } as z.infer<typeof schema>,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    listeners: {
      onChange: ({ fieldApi, formApi }) => {
        if (fieldApi.name === "matchKind") {
          const sets =
            fieldApi.state.value === "set"
              ? [
                  {
                    winScore: 28,
                    switchScore: 7,
                  },
                ]
              : [
                  {
                    winScore: 21,
                    switchScore: 7,
                  },
                  {
                    winScore: 21,
                    switchScore: 7,
                  },
                  {
                    winScore: 15,
                    switchScore: 5,
                  },
                ];

          formApi.setFieldValue("sets", sets);
        }
      },
    },
    onSubmit: ({
      value: {
        teamsPerPool,
        wildcardCount,
        sets,
        scheduledTime,
        assignCourts,
        autoAssignWildcards,
        overwrite,
      },
    }) => {
      mutate({
        id: division.id,
        teamsPerPool,
        wildcardCount,
        assignCourts,
        autoAssignWildcards,
        overwrite,
        scheduledTime: scheduledTime?.toString() ?? null,
        sets,
      });
    },
  });

  const teams = useActiveTeams(division.id);
  const { data: pools } = useQuery(
    trpc.tournaments.getPools.queryOptions({
      tournamentDivisionId: division.id,
    }),
  );
  const poolCount = pools?.length ?? 0;

  const { data: playoffs } = useQuery(
    trpc.tournaments.getPlayoffs.queryOptions({
      tournamentDivisionId: division.id,
    }),
  );
  const hasExistingPlayoffs = (playoffs?.length ?? 0) > 0;

  const teamCount = teams?.length ?? "loading...";

  return (
    <Modal {...props} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Create Playoffs
        </Heading>

        <p className="mb-6 text-sm text-gray-700">
          Create the playoff bracket for the{" "}
          <span className="font-bold italic">
            {getTournamentDivisionDisplay(division)}
          </span>{" "}
          division. If you want to recreate the bracket entirely, select{" "}
          <span className="font-semibold italic">Overwrite existing</span>.
        </p>

        {teamCount && (
          <p className="mb-6 text-sm text-gray-700">
            There are <span className="font-bold italic">{teamCount}</span>{" "}
            teams in this division.
          </p>
        )}

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to create playoffs"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppField
            name="teamsPerPool"
            children={(field) => (
              <form.Subscribe
                selector={(state) => state.values.teamsPerPool}
                children={(teamsPerPool) => (
                  <field.Number
                    label={`Teams Advancing per Pool (${teamsPerPool * poolCount} total + wildcards)`}
                    field={field}
                    minValue={1}
                  />
                )}
              />
            )}
          />

          <form.AppField
            name="wildcardCount"
            children={(field) => (
              <form.Subscribe
                selector={(state) => ({
                  teamsPerPool: state.values.teamsPerPool,
                  wildcardCount: state.values.wildcardCount,
                })}
                children={({ teamsPerPool, wildcardCount }) => {
                  const total = teamsPerPool * poolCount + wildcardCount;
                  return (
                    <field.Number
                      label={
                        wildcardCount > 0
                          ? `Wildcard Count (${total} total teams)`
                          : "Wildcard Count"
                      }
                      field={field}
                      minValue={0}
                    />
                  );
                }}
              />
            )}
          />

          <div className="flex flex-col gap-3">
            <form.AppField
              name="matchKind"
              children={(field) => (
                <field.RadioGroup
                  label="Match Style"
                  orientation="horizontal"
                  field={field}
                  mode="string"
                >
                  <Radio value="set">Single Set</Radio>
                  <Radio value="match">Best of 3</Radio>
                </field.RadioGroup>
              )}
            />

            <Disclosure card={false}>
              <DisclosureHeader card={false} size="sm">
                Score Options
              </DisclosureHeader>
              <DisclosurePanel card={false}>
                <form.AppField name="sets" mode="array">
                  {(field) => (
                    <div className="flex flex-col space-y-2">
                      {field.state.value.map((_, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: form array fields keyed by position
                        <div className="grid grid-cols-6 gap-3" key={i}>
                          <form.AppField name={`sets[${i}].winScore`}>
                            {(subField) => (
                              <>
                                <subField.Number
                                  className="col-span-3"
                                  field={subField}
                                  label={i === 0 ? "Win Score" : null}
                                />
                              </>
                            )}
                          </form.AppField>
                          <form.AppField name={`sets[${i}].switchScore`}>
                            {(subField) => (
                              <>
                                <subField.Number
                                  className="col-span-3"
                                  field={subField}
                                  label={i === 0 ? "Switch Every" : null}
                                />
                              </>
                            )}
                          </form.AppField>
                        </div>
                      ))}
                    </div>
                  )}
                </form.AppField>
              </DisclosurePanel>
            </Disclosure>
          </div>

          <form.AppField
            name="assignCourts"
            children={(field) => (
              <field.Checkbox
                label="Assign courts based on pools and seeds"
                field={field}
              />
            )}
          />

          <form.Subscribe
            selector={(state) => state.values.wildcardCount}
            children={(wildcardCount) =>
              wildcardCount > 0 ? (
                <form.AppField
                  name="autoAssignWildcards"
                  children={(field) => (
                    <field.Checkbox
                      label="Auto-assign wildcards when all pools complete"
                      field={field}
                    />
                  )}
                />
              ) : null
            }
          />

          <form.AppField
            name="scheduledTime"
            children={(field) => (
              <field.Time label="Scheduled Start Time" field={field} />
            )}
          />

          <form.AppField
            name="overwrite"
            children={(field) => (
              <field.Checkbox label="Overwrite existing" field={field} />
            )}
          />

          <form.Subscribe selector={(state) => state.values.overwrite}>
            {(overwrite) =>
              hasExistingPlayoffs && !overwrite ? (
                <form.AppForm>
                  <form.Alert
                    color="warning"
                    title="Existing playoffs detected"
                    description="This division already has a playoff bracket. If you are sure you want to recreate it, check 'Overwrite existing'."
                  />
                </form.AppForm>
              ) : null
            }
          </form.Subscribe>

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.Subscribe selector={(state) => state.values.overwrite}>
                {(overwrite) => (
                  <form.SubmitButton
                    requireChange={false}
                    isDisabled={
                      isPending || (hasExistingPlayoffs && !overwrite)
                    }
                  >
                    {isPending ? "Creating…" : "Create"}
                  </form.SubmitButton>
                )}
              </form.Subscribe>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
